import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useThreatHunts, createThreatHunt } from '../../hooks/useSecOps';

interface Hunt {
  id: string;
  title: string;
  hypothesis: string;
  status: string;
  findings_count: number;
  iocs_discovered: number;
  created_at: string;
  started_at?: string;
  completed_at?: string;
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

function Badge({ value, colors }: { value: string; colors: Record<string, string> }) {
  const label = value.replace(/_/g, ' ');
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${colors[value] ?? 'bg-gray-100 text-gray-600'}`}>{label}</span>;
}

export default function ThreatHuntingPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', hypothesis: '', mitre_techniques: '', data_sources: '' });
  const [creating, setCreating] = useState(false);

  const { data, loading, error, refresh } = useThreatHunts(filters);
  const hunts = (data?.hunts ?? []) as Hunt[];

  function setFilter(key: string, value: string) {
    setFilters(f => value ? { ...f, [key]: value } : Object.fromEntries(Object.entries(f).filter(([k]) => k !== key)));
  }

  async function handleCreate() {
    if (!form.title || !form.hypothesis) return;
    setCreating(true);
    try {
      await createThreatHunt({
        title: form.title,
        hypothesis: form.hypothesis,
        mitre_techniques: form.mitre_techniques.split(',').map(t => t.trim()).filter(Boolean),
        data_sources: form.data_sources.split(',').map(s => s.trim()).filter(Boolean),
      });
      setShowCreate(false);
      setForm({ title: '', hypothesis: '', mitre_techniques: '', data_sources: '' });
      refresh();
    } catch { /* handled */ } finally {
      setCreating(false);
    }
  }

  if (loading) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-96 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Threat Hunting</h1>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">{data?.total ?? 0} total</span>
          <button onClick={() => setShowCreate(true)} className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
            New Hunt
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm flex items-center justify-between">{error} <button onClick={refresh} className="text-sm font-medium underline">Retry</button></div>}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 flex flex-wrap gap-3 items-center">
        <select value={filters.status ?? ''} onChange={e => setFilter('status', e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm">
          <option value="">All Statuses</option>
          {['draft', 'in_progress', 'completed', 'cancelled'].map(s => <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}</option>)}
        </select>
        {Object.keys(filters).length > 0 && (
          <button onClick={() => setFilters({})} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">Clear filters</button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-6 py-3">Title</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Hypothesis</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Status</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Findings</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">IOCs</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {hunts.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-400">No threat hunts found. Start a new hunt to proactively search for threats.</td></tr>
            )}
            {hunts.map(hunt => (
              <tr key={hunt.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/secops/threat-hunting/${hunt.id}`)}>
                <td className="px-6 py-4">
                  <p className="text-sm font-medium text-gray-900">{hunt.title}</p>
                </td>
                <td className="px-4 py-4">
                  <p className="text-sm text-gray-600 truncate max-w-xs">{hunt.hypothesis}</p>
                </td>
                <td className="px-4 py-4"><Badge value={hunt.status} colors={STATUS_COLORS} /></td>
                <td className="px-4 py-4 text-sm text-gray-700">{hunt.findings_count ?? 0}</td>
                <td className="px-4 py-4 text-sm text-gray-700">{hunt.iocs_discovered ?? 0}</td>
                <td className="px-4 py-4 text-xs text-gray-500">{hunt.created_at ? new Date(hunt.created_at).toLocaleDateString() : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">New Threat Hunt</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="Hunt campaign title..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hypothesis *</label>
                <textarea value={form.hypothesis} onChange={e => setForm(f => ({ ...f, hypothesis: e.target.value }))}
                  rows={3} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="If an attacker used technique X, we would expect to see Y in Z..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">MITRE Techniques (comma-separated)</label>
                <input type="text" value={form.mitre_techniques} onChange={e => setForm(f => ({ ...f, mitre_techniques: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="T1059, T1078, T1566..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data Sources (comma-separated)</label>
                <input type="text" value={form.data_sources} onChange={e => setForm(f => ({ ...f, data_sources: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="EDR logs, DNS, firewall..." />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button onClick={handleCreate} disabled={creating || !form.title || !form.hypothesis}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50">
                {creating ? 'Creating...' : 'Create Hunt'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
